/** Localizer provides localization functionality (ie pulls text from an appropriate language file).
 * 
 *  REFERENCES:
 *      https://www.polymer-project.org/3.0/toolbox/localize
 *      Polymer.AppLocalizeBehavior:
 *          https://github.com/PolymerElements/app-localize-behavior
 *          https://www.webcomponents.org/element/PolymerElements/app-localize-behavior
 *      Example of customizing Polymer.AppLocalizeBehavior:
 *          https://github.com/StartPolymer/progressive-web-app-template/blob/master/src/app-behaviors/app-localize-behavior.html
 *          http://stackoverflow.com/questions/33469503/extending-custom-behavior
 *      Polymer.AppLocalizeBehavior uses Format.js and applicable syntax:
 *          http://formatjs.io/guides/message-syntax/
 *      SEO considerations:
 *          http://stackoverflow.com/questions/1828317/internationalization-and-search-engine-optimization
 *      Polymer behavior annotation:
 *          https://www.polymer-project.org/1.0/docs/tools/documentation#behaviors
 *
 *  USAGE:
 *      Extend your component from the mixin:
 *          class extends Localizer(Polymer.Element) { ... }
 *  
 *      Add text to appropriate /locales/*.json files.
 *  
 *      Use the localize method in JS:
 *          this.localize('path.to.your.text.in.locales.file');
 *      or in HTML:
 *          [[localize('path.to.your.text.in.locales.file')]]
 *
 *      The property localizeReady exist to indicate if localize has initialized,
 *      it can be observed to trigger updates to relevant UI once localize is ready.
 *
 *  NOTES:
 *      There are more complex/dynamic arguments that can be used,
 *      see AppLocalizeBehavior and Format.js documentations for more.
 */

import { mixinBehaviors } from '@polymer/polymer/lib/legacy/class.js';
import { AppLocalizeBehavior } from '@polymer/app-localize-behavior/app-localize-behavior.js';

import { log } from '../../app/log.js';

let _globalLanguage = null;

export const Localizer = (base) => class extends mixinBehaviors([AppLocalizeBehavior], base) {

    static get properties() {
        return {
            /*
            // Optionally can add custom formatters: http://formatjs.io/guides/message-syntax/#custom-formats
            formats: {
                type: Object,
                value: function () { return {} }
            },
            */

            // The language property is used by AppLocalizeBehavior (per its convention) to know what language is set.
            language: {
                type: String,
                computed: '_determineLanguage()'
            },
            localizeReady: {
                type: Boolean,
                value: null
            }
        }
    }

    connectedCallback() {
        super.connectedCallback();

        // Add event listeners for loadResources call success/failure.
        this.addEventListener('app-localize-resources-loaded', this._localizeLoaded);
        this.addEventListener('app-localize-resources-error', this._localizeFailed);
    }
    disconnectedCallback() {
        super.disconnectedCallback();

        // Remove event listeners.
        this.removeEventListener('app-localize-resources-loaded', this._localizeLoaded);
        this.removeEventListener('app-localize-resources-error', this._localizeFailed);
    }

    _localizeLoaded() {
        this.localizeReady = true;
    }
    _localizeFailed() {
        this.localizeReady = false;
        log.error('Localizer._localizeFailed', this.language);
    }

    _determineLanguage() {
        // If lang (_globalLanguage) has not been set then we need to determine the language to use.
        if (!_globalLanguage) {
            // Get language from browser.
            // http://stackoverflow.com/questions/1043339/javascript-for-detecting-browser-language-preference/38150585
            _globalLanguage =
                (navigator.languages && navigator.languages[0]) || // Good browsers (Chrome / Firefox).
                navigator.language || // Almost all browsers.
                navigator.userLanguage; // IE <= 10

            // TODO: Get language from some other user set metric? like from the URL: example.com/fr-CA/<route>
            // https://support.google.com/webmasters/answer/6144055?hl=en

            // Normalize to lowercase for consistency, important for lookups/etc.
            _globalLanguage = (_globalLanguage || '').toLowerCase();
        }

        // Ensure the language is loaded.
        // The _globalLanguage might be set but it doesn't mean the language has been loaded for this component,
        // though AppLocalizerBehavior will reuse previously loaded language resources (by other components) for efficiency.
        this._ensureLanguageLoaded(_globalLanguage);

        return _globalLanguage;
    }
    _ensureLanguageLoaded(lang) {
        if (lang && !this._languageLoaded) {
            // Load the language resources.
            this.loadResources('/localization/' + lang);

            this._languageLoaded = true;
        }
    }

};